import React, { useState, useMemo } from 'react';
import './TemplateDeliverabilityChecker.css';

const CATEGORY_LABELS = {
  money: 'Money & Benefits',
  urgency: 'Urgency & Pressure',
  certainty: 'Exaggerated Certainty',
  cta: 'Hard Sales CTA',
};

// Real promotional keywords a filter would count. Formatting (CAPS, !!!, $$$)
// is handled by dedicated heuristics below instead of duplicating it here.
// `w` = strength (how heavily inbox providers weight this word): 2 = strong,
// 1.5 = medium, 1 = mild. Strong words hurt deliverability much more.
const SPAM_PATTERNS = [
  // Money & Benefits
  { pattern: /\b(free|gratis|kostenlos|kostenfrei)\b/gi, label: 'Free/Gratis', category: 'money', w: 2 },
  { pattern: /\b(discount|rabatt|sonderangebot|special\s+offer|sale)\b/gi, label: 'Discount/Offer', category: 'money', w: 2 },
  { pattern: /\b(bonus|gutschein|voucher|coupon)\b/gi, label: 'Bonus/Voucher', category: 'money', w: 2 },
  { pattern: /\b(sparen|spare|save\s+money|geld\s+sparen)\b/gi, label: 'Save money', category: 'money', w: 1.5 },
  { pattern: /\b(gewinn|gewinnen|prize)\b/gi, label: 'Win/Prize', category: 'money', w: 2 },
  { pattern: /\b(make\s+money|geld\s+verdienen|passives\s+einkommen|passive\s+income)\b/gi, label: 'Money promise', category: 'money', w: 2 },
  { pattern: /\b(umsatzsteigerung|umsatz\s+steigern|mehr\s+umsatz|boost\s+(your\s+)?(sales|revenue)|increase\s+revenue)\b/gi, label: 'Revenue boost', category: 'money', w: 1 },
  { pattern: /\b(mehr\s+kunden|neukunden|more\s+customers|new\s+customers)\b/gi, label: 'More customers', category: 'money', w: 1 },
  { pattern: /\b(mehr\s+leads|more\s+leads|leads\s+generieren|generate\s+leads)\b/gi, label: 'More leads', category: 'money', w: 1 },

  // Urgency & Pressure
  { pattern: /\b(sofort|umgehend|immediately|instantly)\b/gi, label: 'Immediacy', category: 'urgency', w: 1.5 },
  { pattern: /\b(dringend|dringlich|urgent)\b/gi, label: 'Urgent', category: 'urgency', w: 1.5 },
  { pattern: /\b(nur\s+heute|only\s+today|heute\s+noch|act\s+now|jetzt\s+handeln|limited\s+time|jetzt\s+zuschlagen)\b/gi, label: 'Act now / today only', category: 'urgency', w: 1.5 },
  { pattern: /\b(letzte\s+chance|last\s+chance|once\s+in\s+a\s+lifetime|einmalige\s+chance|nie\s+wieder|jetzt\s+oder\s+nie)\b/gi, label: 'Last chance', category: 'urgency', w: 2 },
  { pattern: /\b(begrenzte\s+plätze|nur\s+noch\s+wenige|limited\s+spots|limited\s+seats|begrenzte\s+verfügbarkeit)\b/gi, label: 'Limited availability', category: 'urgency', w: 1.5 },
  { pattern: /\b(läuft\s+ab|verfällt|angebot\s+endet|expires?|deadline|bald\s+vorbei)\b/gi, label: 'Expiring offer', category: 'urgency', w: 1.5 },

  // Exaggerated Certainty
  { pattern: /\b(guarantee[d]?|garantie|garantiert|erfolgsgarantie)\b/gi, label: 'Guarantee', category: 'certainty', w: 2 },
  { pattern: /\b(risikofrei|risk.?free|ohne\s+risiko|kein\s+risiko)\b/gi, label: 'Risk-free', category: 'certainty', w: 1.5 },
  { pattern: /\b(bewiesen|nachweislich|proven|proof)\b/gi, label: 'Proven', category: 'certainty', w: 1.5 },
  { pattern: /\b(sicherer\s+erfolg|garantierter\s+erfolg|guaranteed\s+success)\b/gi, label: 'Guaranteed success', category: 'certainty', w: 2 },
  { pattern: /(100\s*%|100\s+prozent|hundert\s+prozent|double\s+your|verdoppeln)/gi, label: '100% / Exaggeration', category: 'certainty', w: 2 },
  { pattern: /\b(unverbindlich|ohne\s+verpflichtung|no\s+obligation)\b/gi, label: 'No obligation', category: 'certainty', w: 1.5 },

  // Hard Sales CTA
  { pattern: /\b(jetzt\s+kaufen|buy\s+now|order\s+now|jetzt\s+bestellen|bestellen\s+sie)\b/gi, label: 'Buy now', category: 'cta', w: 2 },
  { pattern: /\b(jetzt\s+buchen|book\s+now|demo\s+buchen|book\s+a\s+demo)\b/gi, label: 'Book now / demo', category: 'cta', w: 1.5 },
  { pattern: /\b(termin\s+sichern|angebot\s+sichern|platz\s+sichern|jetzt\s+sichern|secure\s+your\s+(spot|seat))\b/gi, label: 'Secure your spot', category: 'cta', w: 1.5 },
  { pattern: /\b(hier\s+klicken|click\s+here|click\s+now|jetzt\s+klicken)\b/gi, label: 'Click here', category: 'cta', w: 2 },
  { pattern: /\b(unverbindlich\s+anfragen|jetzt\s+anfragen|request\s+now|jetzt\s+kontaktieren)\b/gi, label: 'Request now', category: 'cta', w: 1.5 },
];

const SALES_CATEGORIES = ['money', 'urgency', 'certainty', 'cta'];

const URL_SHORTENER_RE = /\b(?:bit\.ly|tinyurl\.com|goo\.gl|t\.co|ow\.ly|buff\.ly|cutt\.ly|rebrand\.ly|is\.gd|rb\.gy|shorturl\.at|lnkd\.in|tiny\.cc|soo\.gd)\b/gi;
const EMOJI_RE = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{2190}-\u{21FF}\u{2705}\u{274C}\u{2714}\u{2728}]/gu;
const GENERIC_GREETING_RE = /(sehr\s+geehrte\s+damen\s+und\s+herren|dear\s+sir\s+or\s+madam|to\s+whom\s+it\s+may\s+concern|hallo\s+zusammen|guten\s+tag\s+zusammen)/i;
const PHISHING_RE = /\b(verify\s+your\s+account|confirm\s+your\s+(account|password|payment|identity)|update\s+your\s+(payment|billing|account)|konto\s+(verifizieren|bestätigen)|passwort\s+(bestätigen|zurücksetzen)|suspicious\s+activity|unusual\s+activity|account\s+(suspended|locked)|zahlung\s+bestätigen)\b/gi;

const SEVERITY_WEIGHT = { high: 3, medium: 1.6, low: 0.6 };

const SEVERITY_META = {
  high: { color: '#ef4444', icon: '✗' },
  medium: { color: '#f59e0b', icon: '⚠' },
  low: { color: '#64748b', icon: '•' },
};

const countWords = (text) => (text || '').split(/\s+/).filter(Boolean).length;
const countMatches = (text, re) => (text.match(re) || []).length;

// Placeholders/instructions like {name} or {{ ... }} are directives for the AI,
// not real email copy, so they must not be counted as spam words.
const stripInstructions = (text) => (text || '')
  .replace(/\{\{[\s\S]*?\}\}/g, ' ')
  .replace(/\{[\s\S]*?\}/g, ' ');

const isPersonalized = (rawText) => /\{[^}]*\}|\{\{[^}]*\}\}|\[(name|vorname|firma|company|first_?name)\]/i.test(rawText || '');

const groupWords = (words) => {
  const map = new Map();
  words.forEach((w) => {
    const key = w.toLowerCase();
    const existing = map.get(key);
    if (existing) existing.count += 1;
    else map.set(key, { word: w, count: 1 });
  });
  return Array.from(map.values());
};

const scanSpam = (cleaned) => {
  const hits = [];
  const categories = new Set();
  const allWords = [];
  let count = 0;
  let strength = 0;
  for (const sp of SPAM_PATTERNS) {
    const matches = cleaned.match(sp.pattern);
    if (matches && matches.length) {
      const words = matches.map((m) => m.replace(/\s+/g, ' ').trim()).filter(Boolean);
      count += matches.length;
      strength += (sp.w || 1) * matches.length;
      categories.add(sp.category);
      hits.push({ label: sp.label, category: sp.category, words });
      allWords.push(...words);
    }
  }
  return { count, strength, hits, categories, allWords };
};

const sampleWords = (words, n = 3) => words.slice(0, n).map((w) => `"${w}"`).join(', ');

// Reusable promotional-word scan (used by the footer spam checker too).
export const scanPromotionalWords = (text) => {
  const { count, strength, allWords } = scanSpam(stripInstructions(text || ''));
  return { count, strength, words: allWords };
};

// ── Inbox brain: score one email body the way Gmail/Outlook would ──
const analyzeEmailContent = (rawText) => {
  const text = rawText || '';
  const cleaned = stripInstructions(text);
  const words = cleaned.split(/\s+/).filter(Boolean);
  const wordCount = words.length;
  const findings = [];
  const add = (severity, title, detail, chips, weight) => findings.push({ severity, title, detail, chips, weight });

  const spam = scanSpam(cleaned);
  const personalized = isPersonalized(text);

  // 1) Promotional keyword load — weighted by how strong the words are, so two
  // strong triggers hurt far more than two mild ones.
  const spamPenalty = spam.count ? Math.min(7, spam.strength * 1.4) : 0;
  if (spam.count > 0) {
    const severity = spamPenalty >= 4.5 ? 'high' : spamPenalty >= 2.4 ? 'medium' : 'low';
    const strongWords = spam.hits.filter((h) => (SPAM_PATTERNS.find((p) => p.label === h.label)?.w || 1) >= 2);
    const noun = spam.count === 1 ? 'promotional trigger word' : 'promotional trigger words';
    let detail;
    if (severity === 'high') {
      detail = 'Inbox filters tally promotional keywords and weight the strong ones heavily. This combination is a clear "Promotions/Spam" signal.';
    } else if (severity === 'medium') {
      detail = strongWords.length
        ? 'These include strong promotional words that providers weight heavily — even a couple noticeably lowers inbox placement.'
        : 'A few sales keywords together nudge the email toward the Promotions tab.';
    } else {
      detail = `${sampleWords(spam.allWords)} on its own is mild — just avoid stacking more.`;
    }
    add(severity, `${spam.count} ${noun}`, detail, groupWords(spam.allWords), spamPenalty);
  }

  // 2) Category stacking — the classic "sales blast" fingerprint
  const salesCats = SALES_CATEGORIES.filter((c) => spam.categories.has(c));
  if (salesCats.length >= 3) {
    add('high', 'Reads like a sales blast',
      `You combine ${salesCats.map((c) => CATEGORY_LABELS[c]).join(' + ')}. That money + urgency + hard-CTA cocktail is exactly what Gmail/Outlook push to spam.`);
  } else if (salesCats.length === 2) {
    add('medium', 'Sales-y tone stacking',
      `Combines ${salesCats.map((c) => CATEGORY_LABELS[c]).join(' + ')}. Two promotional angles in one cold email read salesy — soften one.`);
  }

  // 3) Links
  const linkCount = countMatches(cleaned, /https?:\/\//gi);
  const shorteners = cleaned.match(URL_SHORTENER_RE) || [];
  if (shorteners.length) {
    add('high', 'URL shortener detected',
      `Links like "${shorteners[0]}" hide the real destination — a phishing/spam signal for cold mail. Use the full branded URL.`);
  }
  if (linkCount > 3) {
    add('high', `${linkCount} links`, 'Many links in a first email looks like a newsletter/ad. Keep it to a single relevant link.');
  } else if (linkCount > 1) {
    add('medium', `${linkCount} links`, 'More than one link raises spam scoring. Ideally link only once.');
  }
  if (linkCount >= 1 && wordCount > 0 && wordCount < 40) {
    add('medium', 'Mostly a link', 'Very little text around a link looks like a drive-by promo. Add genuine, personal context.');
  }

  // 4) Shouting in CAPS (ignore short acronyms like CEO/SEO/B2B)
  const capsWords = words.filter((w) => {
    const letters = w.replace(/[^A-Za-zÄÖÜ]/g, '');
    return letters.length >= 5 && w === w.toUpperCase() && /[A-ZÄÖÜ]/.test(w);
  });
  if (capsWords.length >= 2) {
    add('medium', 'Shouting in CAPS', `${capsWords.length} all-caps words (e.g. "${capsWords[0]}"). Caps read as shouting and are a known spam trigger.`);
  }

  // 5) Punctuation
  if (/!{2,}/.test(cleaned)) {
    add('medium', 'Stacked "!!!"', 'Multiple exclamation marks in a row are a top spam signal. Use at most one.');
  } else if (countMatches(cleaned, /!/g) >= 3) {
    add('low', 'Lots of exclamation marks', 'Many "!" read as hype. One is plenty for a professional email.');
  }

  // 6) Emojis
  const emojis = cleaned.match(EMOJI_RE) || [];
  if (emojis.length >= 3) {
    add('medium', `${emojis.length} emojis`, 'Several emojis make a cold email look like a marketing blast. Drop most of them.');
  } else if (emojis.length >= 1) {
    add('low', 'Emoji used', 'Emojis in cold B2B mail can read as promotional. Use sparingly.');
  }

  // 7) Prices / discounts
  const pricey = cleaned.match(/(\d+\s?%|[$€£]\s?\d|\d+\s?(?:€|eur|euro|usd|dollar))/gi) || [];
  if (pricey.length) {
    add('medium', 'Prices / discounts', `Figures like "${pricey[0].trim()}" make it look like an offer. Cold outreach performs better without price/discount talk.`);
  }

  // 8) Phishing-style wording
  const phish = cleaned.match(PHISHING_RE);
  if (phish) {
    add('high', 'Phishing-style wording', `Phrases like "${phish[0]}" mimic account/security scams and are aggressively filtered. Use plain, human language.`);
  }

  // 9) Generic salutation
  if (GENERIC_GREETING_RE.test(cleaned)) {
    add('low', 'Generic salutation', 'A generic greeting ("Sehr geehrte Damen und Herren" / "Dear Sir or Madam") signals mass mail. Address the person directly.');
  }

  // 10) Length
  if (wordCount === 0) {
    add('high', 'Empty variant', 'This variant has no body text.');
  } else if (wordCount < 20) {
    add('high', 'Too short', `Only ${wordCount} words. Ultra-short cold emails look automated and generic to filters and people.`);
  } else if (wordCount < 50) {
    add('low', 'A bit short', `${wordCount} words. 50–150 words tends to feel personal and land better.`);
  } else if (wordCount > 500) {
    add('low', 'Very long', `${wordCount} words. Long walls of text lower replies — keep it under ~200.`);
  }

  // 11) Personalization
  if (!personalized && wordCount > 0) {
    add('low', 'No personalization', 'No placeholders like {name}/{company}. Identical text to everyone reads as bulk mail.');
  }

  const hasQuestion = /\?/.test(cleaned);

  let score = 10;
  findings.forEach((f) => { score -= (f.weight != null ? f.weight : SEVERITY_WEIGHT[f.severity]); });
  // Only reward genuinely clean copy — a promotional email shouldn't be lifted
  // back into the "safe" zone by a question mark or a placeholder.
  if (spam.count === 0) {
    if (hasQuestion) score += 0.4;
    if (personalized) score += 0.4;
  }
  score = Math.max(1, Math.min(10, Math.round(score * 10) / 10));

  return { rawText: text, wordCount, linkCount, findings, score, spamCount: spam.count, personalized };
};

const analyzeSubjectContent = (rawText) => {
  const text = (rawText || '').trim();
  const cleaned = stripInstructions(text);
  const charCount = text.length;
  const findings = [];
  const add = (severity, title, detail, chips, weight) => findings.push({ severity, title, detail, chips, weight });

  const spam = scanSpam(cleaned);
  // Subjects are read first and weighted more heavily than body text.
  const spamPenalty = spam.count ? Math.min(7, spam.strength * 1.7) : 0;
  if (spam.count > 0) {
    const severity = spamPenalty >= 3 ? 'high' : spamPenalty >= 1.5 ? 'medium' : 'low';
    add(severity,
      spam.count === 1 ? 'Spam word in subject' : `${spam.count} spam words in subject`,
      'The subject is the first thing a filter reads and is weighted heavily — promotional words here strongly push to spam/promotions.',
      groupWords(spam.allWords), spamPenalty);
  }

  if (/^\s*(re|fw|fwd|aw|wg)\s*:/i.test(text)) {
    add('high', 'Fake Re:/Fwd: prefix', 'Faking a reply/forward to a cold contact is a classic spam trick and erodes trust the moment they notice.');
  }

  const capsWords = cleaned.split(/\s+/).filter((w) => {
    const letters = w.replace(/[^A-Za-zÄÖÜ]/g, '');
    return letters.length >= 5 && w === w.toUpperCase() && /[A-ZÄÖÜ]/.test(w);
  });
  if (capsWords.length >= 1) {
    add('medium', 'CAPS in subject', 'All-caps subjects scream spam. Use normal casing.');
  }

  if (/!{2,}|\?{2,}|!\?|\?!/.test(text)) {
    add('medium', 'Excessive punctuation', 'Stacked !/? in a subject is a strong spam signal.');
  } else if (countMatches(text, /!/g) >= 1) {
    add('low', 'Exclamation in subject', 'Exclamation marks in subjects can trip promo filters. Consider removing.');
  }

  if ((text.match(EMOJI_RE) || []).length) {
    add('low', 'Emoji in subject', 'Emojis in subjects read as marketing — risky for cold B2B.');
  }

  if (charCount === 0) {
    add('high', 'Empty subject', 'No subject line — these rarely get opened and are often filtered.');
  } else if (charCount < 20) {
    add('low', 'Short subject', `${charCount} chars. Aim for ~30–60 for a natural, human subject.`);
  } else if (charCount > 60) {
    add('medium', 'Long subject', `${charCount} chars — gets cut off in the inbox (esp. mobile) and can look spammy.`);
  }

  let score = 10;
  findings.forEach((f) => { score -= (f.weight != null ? f.weight : SEVERITY_WEIGHT[f.severity]); });
  score = Math.max(1, Math.min(10, Math.round(score * 10) / 10));

  return { rawText: text, charCount, findings, score, spamCount: spam.count };
};

const mean = (arr) => (arr.length ? arr.reduce((sum, x) => sum + x, 0) / arr.length : 0);

export function analyzeDeliverability(variants, subjectLines) {
  const allSubjects = (subjectLines || []).map((s) => (typeof s === 'string' ? s : s?.text || '')).filter((s) => s && s.trim());
  const allVariants = (variants || []).map((v) => (typeof v === 'string' ? v : v?.text || '')).filter((v) => v && v.trim());

  if (!allVariants.length && !allSubjects.length) return null;

  const variantAnalyses = allVariants.map((text, idx) => ({ index: idx, ...analyzeEmailContent(text) }));
  const subjectAnalyses = allSubjects.map((text, idx) => ({ index: idx, ...analyzeSubjectContent(text) }));

  const avgSpamPerVariant = mean(variantAnalyses.map((v) => v.spamCount));
  const avgSpamPerSubject = mean(subjectAnalyses.map((s) => s.spamCount));
  const typicalEmailSpam = avgSpamPerVariant + avgSpamPerSubject;
  const maxSpamInVariant = variantAnalyses.reduce((max, v) => Math.max(max, v.spamCount), 0);
  const spamWordCount = variantAnalyses.reduce((a, v) => a + v.spamCount, 0) + subjectAnalyses.reduce((a, s) => a + s.spamCount, 0);

  const hasPersonalization = variantAnalyses.length ? variantAnalyses.some((v) => v.personalized) : false;
  const linkCount = variantAnalyses.reduce((max, v) => Math.max(max, v.linkCount), 0);

  const avgVariantScore = variantAnalyses.length ? mean(variantAnalyses.map((v) => v.score)) : 8;
  const avgSubjectScore = subjectAnalyses.length ? mean(subjectAnalyses.map((s) => s.score)) : avgVariantScore;
  const worstVariantScore = variantAnalyses.length ? Math.min(...variantAnalyses.map((v) => v.score)) : avgVariantScore;

  // Overall: mostly the body variants, with the subject as a meaningful factor.
  const rawScore = subjectAnalyses.length ? avgVariantScore * 0.75 + avgSubjectScore * 0.25 : avgVariantScore;
  const score = Math.max(1, Math.min(10, Math.round(rawScore)));

  const totalFindings = variantAnalyses.reduce((a, v) => a + v.findings.length, 0) + subjectAnalyses.reduce((a, s) => a + s.findings.length, 0);

  return {
    score,
    variantAnalyses,
    subjectAnalyses,
    typicalEmailSpam,
    avgSpamPerVariant,
    avgSpamPerSubject,
    maxSpamInVariant,
    spamWordCount,
    hasPersonalization,
    linkCount,
    worstVariantScore,
    totalFindings,
  };
}

const getEmailVerdict = (score) => {
  if (score >= 7) return { status: 'ok', label: 'Inbox-safe', color: '#22c55e' };
  if (score >= 4) return { status: 'warn', label: 'Might get filtered', color: '#f59e0b' };
  return { status: 'danger', label: 'Likely spam', color: '#ef4444' };
};

const getSpamRisk = (count) => {
  if (count < 2) return { status: 'ok', label: 'Low' };
  if (count < 4) return { status: 'warn', label: 'Medium' };
  if (count < 6) return { status: 'danger', label: 'High' };
  return { status: 'danger', label: 'Very High' };
};

const Finding = ({ finding }) => {
  const meta = SEVERITY_META[finding.severity];
  return (
    <div className="tdc-finding">
      <span className={`tdc-finding-badge tdc-finding-badge--${finding.severity}`}>{meta.icon}</span>
      <div className="tdc-finding-body">
        <div className="tdc-finding-title">{finding.title}</div>
        <div className="tdc-finding-detail">{finding.detail}</div>
        {finding.chips && finding.chips.length > 0 && (
          <div className="tdc-finding-chips">
            {finding.chips.map((c, i) => (
              <span key={i} className="tdc-finding-chip">
                {c.word}{c.count > 1 ? ` ×${c.count}` : ''}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EmailCard = ({ title, meta, analysis, cleanText }) => {
  const verdict = getEmailVerdict(analysis.score);
  const highCount = analysis.findings.filter((f) => f.severity === 'high').length;
  const mediumCount = analysis.findings.filter((f) => f.severity === 'medium').length;
  const hasFindings = analysis.findings.length > 0;
  const [open, setOpen] = useState(true);

  const issueSummary = [];
  if (highCount) issueSummary.push(`${highCount} critical`);
  if (mediumCount) issueSummary.push(`${mediumCount} warning${mediumCount > 1 ? 's' : ''}`);
  const lowCount = analysis.findings.length - highCount - mediumCount;
  if (lowCount) issueSummary.push(`${lowCount} minor`);

  return (
    <div className={`tdc-vcard tdc-vcard--${verdict.status}`}>
      <div className="tdc-vcard-head" onClick={() => hasFindings && setOpen(!open)} style={{ cursor: hasFindings ? 'pointer' : 'default' }}>
        <span className="tdc-vcard-dot" style={{ background: verdict.color }} />
        <span className="tdc-vcard-title">{title}</span>
        <span className="tdc-vcard-meta">{meta}</span>
        <span className="tdc-vcard-issues">{hasFindings ? issueSummary.join(' · ') : 'no flags'}</span>
        <span className={`tdc-vcard-verdict tdc-vcard-verdict--${verdict.status}`}>
          {verdict.label} · {analysis.score}/10
        </span>
        {hasFindings && <span className="tdc-vcard-chevron">{open ? '▲' : '▼'}</span>}
      </div>
      {!hasFindings && (
        <div className="tdc-vcard-clean">✓ {cleanText}</div>
      )}
      {hasFindings && open && (
        <div className="tdc-vcard-findings">
          {analysis.findings.map((f, i) => <Finding key={i} finding={f} />)}
        </div>
      )}
    </div>
  );
};

const TemplateDeliverabilityChecker = ({ emailText, subjectLine = '', variants, subjectVariants }) => {
  const [expanded, setExpanded] = useState(false);

  const result = useMemo(
    () => {
      const resolvedVariants = variants || (emailText ? [{ text: emailText }] : []);
      const resolvedSubjects = subjectVariants || (subjectLine ? [{ text: subjectLine }] : []);
      return analyzeDeliverability(resolvedVariants, resolvedSubjects);
    },
    [variants, subjectVariants, emailText, subjectLine]
  );

  if (!result) return null;

  const getColor = (s) => (s >= 7 ? '#22c55e' : s >= 4 ? '#f59e0b' : '#ef4444');
  const getLabel = (s) => (s >= 8 ? 'Excellent' : s >= 6 ? 'Good' : s >= 4 ? 'Needs Work' : 'High Risk');
  const getSummary = (s) => {
    if (s >= 8) return 'Overall this reads like genuine personal mail and should reach the inbox reliably.';
    if (s >= 6) return 'Mostly solid. A few variants have weak spots that could nudge them toward Promotions — see below.';
    if (s >= 4) return 'Several variants show patterns that Gmail/Outlook associate with spam. Fix the flagged variants below.';
    return 'High risk: multiple variants look like promotional/spam mail to inbox providers. Rework the flagged variants.';
  };

  const color = getColor(result.score);
  const spamRisk = getSpamRisk(result.typicalEmailSpam);
  const roundedTypicalSpam = Math.round(result.typicalEmailSpam * 10) / 10;
  const worstVerdict = getEmailVerdict(result.worstVariantScore);
  const variantCount = result.variantAnalyses.length;

  const glance = [{ label: 'Variants', value: `${variantCount}`, status: 'ok' }];
  if (variantCount > 0) {
    glance.push({ label: 'Weakest variant', value: `${result.worstVariantScore}/10`, status: worstVerdict.status });
  }
  glance.push({ label: 'Spam load', value: `~${roundedTypicalSpam}/email · ${spamRisk.label}`, status: spamRisk.status });
  glance.push({ label: 'Personalization', value: result.hasPersonalization ? 'Yes ✓' : 'None', status: result.hasPersonalization ? 'ok' : 'warn' });

  return (
    <div className="tdc-container">
      <div className="tdc-header" onClick={() => setExpanded(!expanded)}>
        <span className="tdc-icon"><i className="fa-solid fa-satellite-dish"></i></span>
        <span className="tdc-title">Deliverability Check</span>
        <span className="tdc-badge" style={{ background: color }}>
          {result.score}/10 — {getLabel(result.score)}
        </span>
        <span className="tdc-chevron">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="tdc-body">
          <div className="tdc-summary">{getSummary(result.score)}</div>

          <div className="tdc-glance">
            {glance.map((g, i) => (
              <div key={i} className={`tdc-glance-item tdc-glance-item--${g.status}`}>
                <span className="tdc-glance-label">{g.label}</span>
                <span className="tdc-glance-value">{g.value}</span>
              </div>
            ))}
          </div>

          {variantCount > 0 && (
            <div className="tdc-section">
              <div className="tdc-section-header">
                <span className="tdc-section-icon"><i className="fa-solid fa-inbox"></i></span>
                <div>
                  <div className="tdc-section-title">Variants — scored like a real inbox</div>
                  <div className="tdc-section-desc">
                    Each variant is judged as if Gmail/Outlook received it. Only one variant is sent per recipient, so they are scored individually — open a variant to see its weak spots.
                  </div>
                </div>
              </div>
              {result.variantAnalyses.map((v, i) => (
                <EmailCard
                  key={v.index}
                  title={`Variant ${i + 1}${variantCount > 1 ? ` / ${variantCount}` : ''}`}
                  meta={`${v.wordCount} words`}
                  analysis={v}
                  cleanText="No weaknesses detected — this reads like a genuine personal email."
                />
              ))}
            </div>
          )}

          {result.subjectAnalyses.length > 0 && (
            <div className="tdc-section">
              <div className="tdc-section-header">
                <span className="tdc-section-icon"><i className="fa-solid fa-envelope"></i></span>
                <div>
                  <div className="tdc-section-title">Subject Lines</div>
                  <div className="tdc-section-desc">
                    The subject is the first thing a filter reads and weighs heavily. Each line is scored on its own.
                  </div>
                </div>
              </div>
              {result.subjectAnalyses.map((s, i) => (
                <EmailCard
                  key={s.index}
                  title={`Subject ${i + 1}${result.subjectAnalyses.length > 1 ? ` / ${result.subjectAnalyses.length}` : ''}`}
                  meta={`${s.charCount} chars`}
                  analysis={s}
                  cleanText="Clean, natural subject — no spam signals detected."
                />
              ))}
            </div>
          )}

          {result.score >= 8 && result.totalFindings === 0 && (
            <div className="tdc-allclear">
              <span>✅</span>
              <span>All clear — no spam signals across any variant. Your template is inbox-ready!</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateDeliverabilityChecker;
